import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Target, Eye, Users } from 'lucide-react';

const AboutPage = () => {
  const [sections, setSections] = useState([]);
  const [heroImage, setHeroImage] = useState({
    image: 'https://images.unsplash.com/photo-1603958956194-cf9718dba4b1',
    title: 'Hakkımızda',
    subtitle: '25 yılı aşkın deneyimimizle geleceğin liderlerini yetiştiriyoruz'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSections();
    fetchHeroImage();
  }, []);

  const fetchHeroImage = async () => {
    try {
      const response = await fetch('http://localhost/api/settings.php?key=about_hero_image', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      if (data.setting_value) {
        try {
          const heroData = typeof data.setting_value === 'string' 
            ? JSON.parse(data.setting_value) 
            : data.setting_value;
          if (heroData.image) {
            setHeroImage(heroData);
          }
        } catch (e) {
          console.error('Error parsing hero image:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching hero image:', error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await fetch('http://localhost/api/pages.php?type=about', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching about sections:', response.statusText);
        setLoading(false);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setSections(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching about sections:', error);
      setLoading(false);
    }
  };

  // Section'ları section key'e göre bul
  const getSection = (sectionKey) => {
    return sections.find(s => s.section === sectionKey);
  };

  const storySection = getSection('story');
  const visionSection = getSection('vision');
  const missionSection = getSection('mission');
  const founderSection = getSection('founder');

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative h-64 mt-20" data-testid="about-hero-section">
        <div className="absolute inset-0">
          <img
            src={heroImage.image}
            alt={heroImage.title || 'Hakkımızda'}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-red-800/70" />
        </div>
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 fade-in-up">
              {heroImage.title || 'Hakkımızda'}
            </h1>
            <p className="text-lg text-gray-100 max-w-2xl fade-in-up" style={{ animationDelay: '0.2s' }}>
              {heroImage.subtitle || '25 yılı aşkın deneyimimizle geleceğin liderlerini yetiştiriyoruz'}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      {storySection && (
        <section className="py-20 bg-gray-50" data-testid="story-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  {storySection.title || 'Hikayemiz'}
                </h2>
                <div 
                  className="space-y-4 text-gray-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: storySection.content || '' }}
                />
              </div>
              {storySection.image_url && (
                <div className="relative">
                  <img
                    src={storySection.image_url}
                    alt={storySection.title || 'Hikayemiz'}
                    className="rounded-2xl shadow-2xl w-full"
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Vision & Mission */}
      {(visionSection || missionSection) && (
        <section className="py-20 bg-white" data-testid="vision-mission-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Vision */}
              {visionSection && (
                <div className="p-8 rounded-2xl bg-gradient-to-br from-red-900/10 to-red-800/10 border-l-4 border-red-900">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-900 flex items-center justify-center mr-4">
                      <Eye className="text-white" size={24} />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">{visionSection.title || 'Vizyonumuz'}</h3>
                  </div>
                  <div 
                    className="text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: visionSection.content || '' }}
                  />
                </div>
              )}

              {/* Mission */}
              {missionSection && (
                <div className="p-8 rounded-2xl bg-[#faf8f3] border-l-4 border-[#C0A062]">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#C0A062] flex items-center justify-center mr-4">
                      <Target className="text-white" size={24} />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">{missionSection.title || 'Misyonumuz'}</h3>
                  </div>
                  <div 
                    className="text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: missionSection.content || '' }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Founder's Message */}
      {founderSection && (
        <section className="py-20 bg-white" data-testid="founder-message-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-red-900 to-red-800 rounded-3xl p-8 md:p-12 text-white">
              <div className="flex flex-col md:flex-row items-center gap-8">
                {founderSection.image_url ? (
                  <div className="w-40 h-40 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={founderSection.image_url}
                      alt={founderSection.title || 'Kurucu'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-40 h-40 rounded-full bg-[#C0A062] flex items-center justify-center flex-shrink-0">
                    <Users size={80} className="text-white" />
                  </div>
                )}
                <div>
                  <h3 className="text-3xl font-bold mb-4">{founderSection.title || 'Kurucumuzun Mesajı'}</h3>
                  <div 
                    className="text-red-100 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: founderSection.content || '' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {loading && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-600">Yükleniyor...</p>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default AboutPage;